import {
  Box,
  Flex,
  Text,
  Badge,
  Spinner,
  Avatar,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Button,
} from "@chakra-ui/react";
import { ArrowBackIcon, AddIcon, MinusIcon } from "@chakra-ui/icons";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../../Navbar/Navbar";
import Sidebar from "../../Sidebar";
import AdjustWalletModal from "../Modals/AdjustWalletModal";

const SIDEBAR_WIDTH = "240px";

export default function ViewUser() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdjustWalletOpen, setIsAdjustWalletOpen] = useState(false);

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/view/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (data.message === "OK") {
        setUser(data.user);
      }
    } catch (error) {
      console.error("Failed to fetch user", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (!user) {
    return (
      <Flex justify="center" align="center" minH="100vh">
        <Text>User not found</Text>
      </Flex>
    );
  }

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 6 }}
        bg="#f8f9fd"
        // minH="100vh"
        mt="60px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        <Flex justify="flex-start" align="center" mb={6}>
          <Button
            as={Link}
            to="/users"
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
          >
            Back to All Users
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {/* Profile Card */}
          <Card bg="white" shadow="sm" borderRadius="lg">
            <CardBody textAlign="center">
              <Avatar
                size="2xl"
                name={user.name || user.username}
                src={user.avatar}
                mb={4}
              />
              <Heading size="md" mb={2}>
                {user.name || "No Name"}
              </Heading>
              <Text color="gray.500" fontSize="sm" mb={4}>
                @{user.username}
              </Text>

              <Stack direction="row" justify="center" spacing={4} mb={4}>
                <Badge
                  colorScheme={user.role === "admin" ? "purple" : "blue"}
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {user.role}
                </Badge>
                <Badge
                  colorScheme={user.status === "active" ? "green" : "red"}
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {user.status}
                </Badge>
              </Stack>

              <Divider my={4} />

              <Stack spacing={3}>
                <Stat>
                  <StatLabel>User ID</StatLabel>
                  <StatNumber fontSize="lg">{user.userId}</StatNumber>
                </Stat>
                <Divider />
                <Button 
                    colorScheme="green" 
                    variant="solid" 
                    size="sm" 
                    leftIcon={<AddIcon />}
                    onClick={() => setIsAdjustWalletOpen(true)}
                >
                    Adjust Credits
                </Button>
              </Stack>
            </CardBody>
          </Card>

          {/* Details Card */}
          <Card
            bg="white"
            shadow="sm"
            borderRadius="lg"
            gridColumn={{ md: "span 2" }}
          >
            <CardHeader pb={0}>
              <Heading size="md">User Information</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box>
                  <Text color="gray.500" fontSize="sm">
                    Email
                  </Text>
                  <Text fontWeight="500">{user.email}</Text>
                </Box>
                <Box>
                  <Text color="gray.500" fontSize="sm">
                    Mobile
                  </Text>
                  <Text fontWeight="500">{user.mobile || "N/A"}</Text>
                </Box>
                <Box>
                  <Text color="gray.500" fontSize="sm">
                    Joined At
                  </Text>
                  <Text fontWeight="500">
                    {new Date(user.createdAt).toLocaleString()}
                  </Text>
                </Box>
                <Box>
                  <Text color="gray.500" fontSize="sm">
                    Last Login
                  </Text>
                  <Text fontWeight="500">
                    {user.lastLogin
                      ? new Date(user.lastLogin).toLocaleString()
                      : "Never"}
                    {user.loginMethod && (
                      <Badge ml={2} colorScheme="blue" variant="subtle" textTransform="capitalize">
                        {user.loginMethod}
                      </Badge>
                    )}
                  </Text>
                </Box>
              </SimpleGrid>

              <Divider my={6} />

              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="sm">Wallet</Heading>
                <Button 
                    size="xs" 
                    colorScheme="blue" 
                    onClick={() => setIsAdjustWalletOpen(true)}
                    variant="outline"
                >
                    Adjust
                </Button>
              </Flex>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box p={4} bg="blue.50" borderRadius="md">
                  <Text color="blue.600" fontSize="sm" fontWeight="bold">
                    Wallet Balance
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    ₹{user.wallet?.balance?.toLocaleString("en-IN") || 0}
                  </Text>
                </Box>
                <Box p={4} bg="purple.50" borderRadius="md">
                  <Text color="purple.600" fontSize="sm" fontWeight="bold">
                    Bonus Balance
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    ₹{user.wallet?.bonusBalance?.toLocaleString("en-IN") || 0}
                  </Text>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>
        </SimpleGrid>

        <AdjustWalletModal 
            isOpen={isAdjustWalletOpen} 
            onClose={() => setIsAdjustWalletOpen(false)} 
            user={user} 
            onUpdate={fetchUser} 
        />
      </Box>
    </>
  );
}
